// Public livestream REPLAY viewer, entry module for /live/replay/:eventId
// (Group R · ES2).
//
// WHY THE SRC IS SITE-RELATIVE: `videoUrl` is "/api/live/replay/<id>/video.mp4",
// a route that 302s to a short-lived signed S3 URL. The link we text a viewer
// therefore never expires; only the redirect behind it does, which is the whole
// reason this page does not receive a presigned URL through GraphQL. The path is
// site-relative, NOT root-relative: LiveReplayContent prefixes REACT_APP_API_URL
// before it reaches the <video>, because the bundle is served under
// /staging/quote and /app/quote, never the domain root.
//
// Wrapper + lazy + shell pattern, same as client/src/pages/LiveStreamPage.tsx:
// LiveReplayLoadingShell is a STATIC export so index.tsx can render it as the
// Suspense fallback for BOTH mount sites (the explicit /live/replay/:eventId
// route and the PublicCatchAllDispatcher intercept) while the page itself is
// lazily imported at both.
// See docs/infrastructure/PATIENT_FACING_EMAIL_LINKS.md.
import { Suspense, lazy } from "react";
import { Spin } from "antd";
import { useParams } from "react-router-dom";

// The body (player, Topics and Transcript panel, calls to action) behind the
// lazy boundary.
const LiveReplayContent = lazy(() => import("./LiveReplayContent"));

// Full-viewport loader so a viewer tapping the replay link from a text never
// sees a flash of the quote-funnel landing page while the chunk downloads.
// Exported so the /* catch-all dispatcher in index.tsx can reuse the same
// loading state when it intercepts /live/replay/ at the splat level.
// Inline styles, not Tailwind: this module is in the eager route-table chunk
// and the Tailwind output ships with the lazy one. The dark ground is the
// replay page's own, so the page does not flash white before it paints.
export const LiveReplayLoadingShell = () => (
  <div
    style={{
      minHeight: "100vh",
      backgroundColor: "#0d1117",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flexDirection: "column",
      gap: 16,
    }}
  >
    <Spin size="large" />
    <div style={{ color: "#9aa6b6", fontSize: 14 }}>Loading the replay…</div>
  </div>
);

const LiveReplayPage: React.FC<{
  /** The id parsed out of the URL by PublicCatchAllDispatcher, which mounts
   *  this page OUTSIDE the explicit route and so has no route params to read.
   *  Absent on the explicit route, where `useParams` has it. */
  eventIdOverride?: string;
}> = ({ eventIdOverride }) => {
  const params = useParams<{ eventId?: string }>();
  const eventId = eventIdOverride ?? params.eventId ?? "";

  return (
    // The mount sites in index.tsx already wrap this page in the same shell,
    // but the query suspends a second time after the chunk has loaded, and the
    // boundary that catches it should be labelled wherever the page is mounted
    // from.
    <Suspense fallback={<LiveReplayLoadingShell />}>
      <LiveReplayContent eventId={eventId} />
    </Suspense>
  );
};

export default LiveReplayPage;
